'use client';

import { c as _c } from "react/compiler-runtime";
import { type FormEvent, type ReactNode, useMemo, useState } from 'react';
import { ArrowRight, Sparkles } from 'lucide-react';
import { useLabsStore, usePack } from '@/app/(platform)/(subscription-gated)/labs/_engine/pack-context';
import { BrandButton, Confetti } from '@/app/(platform)/(subscription-gated)/labs/_engine/screens/_components';
import { DurationTag, RecTag } from '@/app/(platform)/(subscription-gated)/labs/_engine/screens/rec-tag';
import { LogoChip, VerticalThumbnail } from '@/app/(platform)/(subscription-gated)/labs/_engine/screens/rec-thumbnail';
import { StepShell } from '@/app/(platform)/(subscription-gated)/labs/_engine/screens/step-shell';
import type { LabsRecommendation } from '@/app/(platform)/(subscription-gated)/labs/_engine/types';
import { LABS_PREVIEW_EVENTS } from '@/lib/analytics/labs-preview-events';
import { cn } from '@/utils/cn';
import { usePreviewAnalytics } from '../_engine/preview-analytics';
import { submitPreviewLead } from '../_engine/submit-preview-lead';
import { PreviewMatchModal } from './preview-match-modal';

/**
 * The public preview's last screen: the result, the externships it points to, and the ask.
 *
 * It occupies the `recommendations` step slot, where the gated sprint shows its own exit screen. The
 * two are different because the reader is different. The sprint sends a paying learner into a program
 * they can start today. This one is talking to someone who arrived from a search result minutes ago
 * and has no account, so every one of the sprint's CTAs would bounce them to /payment.
 *
 * TWO PATHS, NOT A GATE (Jay, 2026-08-26). The result is never withheld. An earlier draft put the
 * matched externships behind the email, and it optimised the wrong thing: signups are what this
 * experiment is judged on, and emails are worth much less while there is no lead endpoint and no
 * nurture list wired up. A gate taxes the reader who was ready to sign up in order to harvest an
 * address from the reader who was not.
 *
 * So the page offers both, in order of what they are worth:
 *   1. Each matched externship opens IN PLACE to show what the work actually is, and the trial CTA
 *      lives inside that panel. Three beats, not one: curious, informed, converted.
 *   2. The email ask sits below, framed as the alternative for someone who is not ready.
 *
 * The cards cannot open the catalog: every program page lives inside (subscription-gated), so a link
 * would bounce a signed-out reader straight to /payment. Expanding in place is how they get the
 * substance anyway, without a navigation that would break the flow or a wall that would end it.
 *
 * Recommendations come from the pack's static fallback, not the live catalog. `useLabsRecommendations`
 * fetches through `useQueryAuth`, which has no session here, so it would fail on every load and land
 * on this same fallback having spent a request and a loading state to get there. The fallback names
 * real programs by contract, so the cards are honest either way.
 */

const SIGN_UP_PATH = '/sign-up';

/**
 * The hook is the TRIAL, not the price (Jay, 2026-08-26). There is no public pricing page, it sits
 * behind the paywall, and leading with a number asks someone who has never heard of Extern to judge a
 * cost before they know the value. Seven days free is the offer.
 *
 * Stated HERE, once, next to the matches rather than inside the overlay. The overlay is the real
 * product component and its button says "Start now"; forking it just to reword one label would mean
 * this page stops tracking whatever the product does next. The card requirement still gets said out
 * loud before anyone taps, which is the part that matters, because finding it at checkout is how
 * goodwill gets spent.
 */
const TRIAL_TERMS = 'Free for 7 days. Card required, cancel any time.';

/**
 * Minimal email shape check, deliberately not the platform's `emailSchema`. This is a marketing
 * capture: over-rejecting a real address costs a signup, while accepting a typo costs one dead row.
 * Permissive is the right direction here.
 */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
interface SignUpParams {
  email?: string;
  externshipId?: number;
  guideSource: string;
  vertical: string;
}

/** Where every action on this screen ends up. UTMs carry the guide, the track and what was clicked. */
function signUpHref({
  email,
  externshipId,
  guideSource,
  vertical
}: SignUpParams): string {
  const params = new URLSearchParams({
    utm_campaign: 'labs_preview',
    utm_content: vertical,
    utm_medium: 'lab',
    utm_source: guideSource
  });
  if (email) {
    params.set('email', email);
  }
  if (externshipId) {
    params.set('externshipId', String(externshipId));
  }
  return `${SIGN_UP_PATH}?${params.toString()}`;
}

/**
 * One matched program. Tapping it opens the detail OVERLAY; it never navigates.
 *
 * It used to send every tap straight to sign-up, which is a bait: a reader taps the one program they
 * were curious about and lands on a bare account form that says nothing about it. Both design reviews
 * scored that as the biggest drop on the page, at the moment intent is highest.
 *
 * The in-place accordion that replaced it was worse to look at: opening a card pushed the rest of the
 * page down and the reader lost their place. The overlay leaves the payoff still underneath.
 */
function MatchCard(t0) {
  const $ = _c(34);
  const {
    accent,
    onOpen,
    rec
  } = t0;
  let t1;
  if ($[0] !== onOpen || $[1] !== rec) {
    t1 = () => onOpen(rec);
    $[0] = onOpen;
    $[1] = rec;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== accent || $[4] !== rec.company || $[5] !== rec.image || $[6] !== rec.logo) {
    t2 = <VerticalThumbnail accent={accent} company={rec.company} logo={rec.logo} src={rec.image} />;
    $[3] = accent;
    $[4] = rec.company;
    $[5] = rec.image;
    $[6] = rec.logo;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== rec.company || $[9] !== rec.logo) {
    t3 = <LogoChip className="absolute -bottom-5 left-4" company={rec.company} logo={rec.logo} />;
    $[8] = rec.company;
    $[9] = rec.logo;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== t2 || $[12] !== t3) {
    t4 = <div className="relative">{t2}{t3}</div>;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== rec.title) {
    t5 = <span className="font-instrument text-[20px] text-fs-text leading-[1.15]">{rec.title}</span>;
    $[14] = rec.title;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== rec.blurb) {
    t6 = <span className="text-fs-body-md text-fs-text-subtle leading-relaxed">{rec.blurb}</span>;
    $[16] = rec.blurb;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== rec.duration) {
    t7 = <DurationTag>{rec.duration}</DurationTag>;
    $[18] = rec.duration;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== rec.tags) {
    t8 = rec.tags.slice(0, 2).map(_temp);
    $[20] = rec.tags;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== t7 || $[23] !== t8) {
    t9 = <div className="flex flex-wrap items-center gap-fs-xs pt-fs-xs">{t7}{t8}</div>;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <span className="flex items-center gap-1 pt-fs-xs font-medium text-fs-body-md text-fs-brand">See what you would actually do<ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" strokeWidth={2} /></span>;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== t5 || $[27] !== t6 || $[28] !== t9) {
    t11 = <div className="flex flex-1 flex-col gap-fs-xs p-fs-md pt-8">{t5}{t6}{t9}{t10}</div>;
    $[26] = t5;
    $[27] = t6;
    $[28] = t9;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== t1 || $[31] !== t11 || $[32] !== t4) {
    t12 = <button className="group flex cursor-pointer flex-col overflow-hidden rounded-fs-card border border-fs-border bg-fs-surface text-left shadow-[0_1px_2px_rgba(10,14,26,0.04)] transition-shadow hover:shadow-[0_8px_24px_rgba(10,14,26,0.08)]" onClick={t1} type="button">{t4}{t11}</button>;
    $[30] = t1;
    $[31] = t11;
    $[32] = t4;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  return t12;
}
function _temp(tag) {
  return <RecTag key={tag}>{tag}</RecTag>;
}
export function PreviewPayoff() {
  const $ = _c(99);
  const {
    archetypes,
    axis,
    deriveArchetype,
    displayName,
    recommendations: config,
    slug
  } = usePack();
  const storedArchetype = useLabsStore(_temp2);
  const m2Answers = useLabsStore(_temp3);
  const {
    guideSource,
    track,
    visitorId
  } = usePreviewAnalytics();
  const [openMatch, setOpenMatch] = useState(null);
  const [email, setEmail] = useState("");
  const [consented, setConsented] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  let t0;
  if ($[0] !== deriveArchetype || $[1] !== m2Answers || $[2] !== storedArchetype) {
    t0 = storedArchetype ?? deriveArchetype(m2Answers);
    $[0] = deriveArchetype;
    $[1] = m2Answers;
    $[2] = storedArchetype;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const archetypeId = t0;
  const arch = archetypes[archetypeId];
  const accent = arch?.accent ?? axis.bothAccent;
  let t1;
  if ($[4] !== archetypeId || $[5] !== config.fallback) {
    t1 = config.fallback[archetypeId] ?? [];
    $[4] = archetypeId;
    $[5] = config.fallback;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== t1) {
    t2 = [...t1];
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const matches = t2;
  let t3;
  if ($[9] !== guideSource || $[10] !== slug) {
    t3 = params => {
      window.location.assign(signUpHref({
        ...params,
        guideSource,
        vertical: slug
      }));
    };
    $[9] = guideSource;
    $[10] = slug;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const goToSignUp = t3;
  let t4;
  if ($[12] !== archetypeId || $[13] !== track) {
    t4 = rec => {
      setOpenMatch(rec);
      track(LABS_PREVIEW_EVENTS.RECOMMENDATION_CLICKED, {
        archetype: archetypeId,
        company: rec.company,
        externship_id: rec.externshipId ?? null,
        externship_title: rec.title
      });
    };
    $[12] = archetypeId;
    $[13] = track;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const handleMatchOpen = t4;
  let t5;
  if ($[15] !== archetypeId || $[16] !== goToSignUp || $[17] !== track) {
    t5 = rec_0 => {
      track(LABS_PREVIEW_EVENTS.SIGNUP_HANDOFF, {
        archetype: archetypeId,
        externship_title: rec_0.title,
        path: "externship_card"
      });
      goToSignUp({
        externshipId: rec_0.externshipId
      });
    };
    $[15] = archetypeId;
    $[16] = goToSignUp;
    $[17] = track;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  const handleStartTrial = t5;
  let t6;
  if ($[19] !== archetypeId || $[20] !== consented || $[21] !== email || $[22] !== goToSignUp || $[23] !== guideSource || $[24] !== slug || $[25] !== track || $[26] !== visitorId) {
    t6 = event => {
      event.preventDefault();
      const trimmed = email.trim();
      if (!EMAIL_RE.test(trimmed)) {
        setError("That email does not look right. Mind checking it?");
        return;
      }
      if (!consented) {
        setError("Check the box and we will send it over.");
        return;
      }
      setError(null);
      setSubmitting(true);
      track(LABS_PREVIEW_EVENTS.EMAIL_SUBMITTED, {
        archetype: archetypeId,
        email: trimmed,
        marketing_consent: true
      });
      submitPreviewLead({
        archetype: archetypeId,
        email: trimmed,
        guideSource,
        marketingConsent: true,
        vertical: slug,
        visitorId
      });
      track(LABS_PREVIEW_EVENTS.SIGNUP_HANDOFF, {
        archetype: archetypeId,
        path: "email_form"
      });
      goToSignUp({
        email: trimmed
      });
    };
    $[19] = archetypeId;
    $[20] = consented;
    $[21] = email;
    $[22] = goToSignUp;
    $[23] = guideSource;
    $[24] = slug;
    $[25] = track;
    $[26] = visitorId;
    $[27] = t6;
  } else {
    t6 = $[27];
  }
  const handleSubmit = t6;
  let t7;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => setOpenMatch(null);
    $[28] = t7;
  } else {
    t7 = $[28];
  }
  let t8;
  if ($[29] !== handleStartTrial || $[30] !== openMatch) {
    t8 = <PreviewMatchModal onClose={t7} onStartTrial={handleStartTrial} rec={openMatch} />;
    $[29] = handleStartTrial;
    $[30] = openMatch;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  let t9;
  if ($[32] !== accent) {
    t9 = <Confetti accent={accent} />;
    $[32] = accent;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  let t10;
  if ($[34] !== accent) {
    t10 = <span aria-hidden={true} className="absolute inset-y-0 left-0 w-1" style={{
      backgroundColor: accent
    }} />;
    $[34] = accent;
    $[35] = t10;
  } else {
    t10 = $[35];
  }
  let t11;
  if ($[36] !== accent) {
    t11 = {
      backgroundColor: accent
    };
    $[36] = accent;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  let t12;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Sparkles className="size-5" strokeWidth={2} />;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  let t13;
  if ($[39] !== t11) {
    t13 = <span aria-hidden={true} className="flex size-10 shrink-0 items-center justify-center rounded-fs-pill text-fs-on-primary" style={t11}>{t12}</span>;
    $[39] = t11;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  let t14;
  if ($[41] !== accent) {
    t14 = <span className="font-medium text-[12px] uppercase leading-[14px] tracking-[0.06em]" style={{
      color: accent
    }}>Your result</span>;
    $[41] = accent;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  let t15;
  if ($[43] !== accent) {
    t15 = {
      color: accent
    };
    $[43] = accent;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  const t16 = arch?.displayName;
  let t17;
  if ($[45] !== t15 || $[46] !== t16) {
    t17 = <span className="font-instrument text-[30px] leading-[1.1] tracking-[-0.5px]" style={t15}>{t16}</span>;
    $[45] = t15;
    $[46] = t16;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  const t18 = arch?.tagline;
  let t19;
  if ($[48] !== t18) {
    t19 = <span className="mt-1 text-fs-body-md text-fs-text-subtle italic">{t18}</span>;
    $[48] = t18;
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  let t20;
  if ($[50] !== t14 || $[51] !== t17 || $[52] !== t19) {
    t20 = <div className="flex flex-col">{t14}{t17}{t19}</div>;
    $[50] = t14;
    $[51] = t17;
    $[52] = t19;
    $[53] = t20;
  } else {
    t20 = $[53];
  }
  let t21;
  if ($[54] !== t13 || $[55] !== t20) {
    t21 = <div className="flex items-center gap-3">{t13}{t20}</div>;
    $[54] = t13;
    $[55] = t20;
    $[56] = t21;
  } else {
    t21 = $[56];
  }
  const t22 = arch?.body;
  let t23;
  if ($[57] !== t22) {
    t23 = <p className="mt-4 text-fs-body-lg text-fs-text-subtle leading-relaxed">{t22}</p>;
    $[57] = t22;
    $[58] = t23;
  } else {
    t23 = $[58];
  }
  let t24;
  if ($[59] !== t10 || $[60] !== t21 || $[61] !== t23) {
    t24 = <div className="relative overflow-hidden rounded-fs-card border border-fs-border bg-fs-surface p-7 shadow-[0_1px_2px_rgba(10,14,26,0.04),0_8px_24px_rgba(10,14,26,0.05)]">{t10}{t21}{t23}</div>;
    $[59] = t10;
    $[60] = t21;
    $[61] = t23;
    $[62] = t24;
  } else {
    t24 = $[62];
  }
  let t25;
  if ($[63] !== accent || $[64] !== displayName || $[65] !== handleMatchOpen || $[66] !== matches) {
    t25 = matches.length > 0 ? <div className="flex flex-col gap-fs-sm"><div className="flex flex-col gap-1"><h2 className="font-instrument text-[24px] text-fs-text leading-[1.15]">Where that takes you in {displayName}</h2><p className="text-fs-body-md text-fs-text-subtle">Real companies, a project you can show people, and a certificate at the end. Open one to see what the work actually is. {TRIAL_TERMS}</p></div><div className={cn("grid gap-fs-md", matches.length > 1 ? "sm:grid-cols-2" : "sm:grid-cols-1")}>{matches.slice(0, 2).map(rec_1 => <MatchCard accent={accent} key={rec_1.title} onOpen={handleMatchOpen} rec={rec_1} />)}</div></div> : null;
    $[63] = accent;
    $[64] = displayName;
    $[65] = handleMatchOpen;
    $[66] = matches;
    $[67] = t25;
  } else {
    t25 = $[67];
  }
  let t26;
  if ($[68] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <div className="flex flex-col gap-1"><h2 className="font-semibold text-fs-text text-fs-title-lg">Not ready to pick one?</h2><p className="text-fs-body-md text-fs-text-subtle">Leave your email and we will send your result and these matches so you can come back to them.</p></div>;
    $[68] = t26;
  } else {
    t26 = $[68];
  }
  let t27;
  if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = e => setEmail(e.target.value);
    $[69] = t27;
  } else {
    t27 = $[69];
  }
  let t28;
  if ($[70] !== email) {
    t28 = <input aria-label="Email" autoComplete="email" className="flex-1 rounded-fs-button border border-fs-border bg-fs-surface px-4 py-3 text-fs-body-lg text-fs-text outline-none focus:border-fs-primary" id="labs-preview-email" inputMode="email" onChange={t27} placeholder="you@university.edu" type="email" value={email} />;
    $[70] = email;
    $[71] = t28;
  } else {
    t28 = $[71];
  }
  const t29 = submitting ? "One moment" : "Send it to me";
  let t30;
  if ($[72] !== submitting || $[73] !== t29) {
    t30 = <BrandButton className="shrink-0" disabled={submitting} type="submit" variant="outline">{t29}</BrandButton>;
    $[72] = submitting;
    $[73] = t29;
    $[74] = t30;
  } else {
    t30 = $[74];
  }
  let t31;
  if ($[75] !== t28 || $[76] !== t30) {
    t31 = <div className="flex flex-col gap-fs-xs sm:flex-row">{t28}{t30}</div>;
    $[75] = t28;
    $[76] = t30;
    $[77] = t31;
  } else {
    t31 = $[77];
  }
  let t32;
  if ($[78] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = e_0 => setConsented(e_0.target.checked);
    $[78] = t32;
  } else {
    t32 = $[78];
  }
  let t33;
  if ($[79] !== consented) {
    t33 = <input checked={consented} className="mt-1 size-4 shrink-0 cursor-pointer" id="labs-preview-consent" onChange={t32} type="checkbox" />;
    $[79] = consented;
    $[80] = t33;
  } else {
    t33 = $[80];
  }
  let t34;
  if ($[81] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <span>Email me my result and new externships in this track. Unsubscribe any time.</span>;
    $[81] = t34;
  } else {
    t34 = $[81];
  }
  let t35;
  if ($[82] !== t33) {
    t35 = <label className="flex items-start gap-fs-xs text-fs-body-md text-fs-text-subtle" htmlFor="labs-preview-consent">{t33}{t34}</label>;
    $[82] = t33;
    $[83] = t35;
  } else {
    t35 = $[83];
  }
  let t36;
  if ($[84] !== error) {
    t36 = error ? <p className="text-fs-body-md text-fs-danger" role="alert">{error}</p> : null;
    $[84] = error;
    $[85] = t36;
  } else {
    t36 = $[85];
  }
  let t37;
  if ($[86] !== handleSubmit || $[87] !== t31 || $[88] !== t35 || $[89] !== t36) {
    t37 = <form className="flex flex-col gap-fs-sm rounded-fs-card border border-fs-border border-dashed bg-fs-surface-subtle p-fs-md" onSubmit={handleSubmit}>{t26}{t31}{t35}{t36}</form>;
    $[86] = handleSubmit;
    $[87] = t31;
    $[88] = t35;
    $[89] = t36;
    $[90] = t37;
  } else {
    t37 = $[90];
  }
  let t38;
  if ($[91] !== t24 || $[92] !== t25 || $[93] !== t37 || $[94] !== t9) {
    t38 = <div className="relative flex flex-col gap-fs-lg py-fs-md">{t9}{t24}{t25}{t37}</div>;
    $[91] = t24;
    $[92] = t25;
    $[93] = t37;
    $[94] = t9;
    $[95] = t38;
  } else {
    t38 = $[95];
  }
  let t39;
  if ($[96] !== t38 || $[97] !== t8) {
    t39 = <StepShell contentAlign="top" showProgress={false}>{t8}{t38}</StepShell>;
    $[96] = t38;
    $[97] = t8;
    $[98] = t39;
  } else {
    t39 = $[98];
  }
  return t39;
}
function _temp3(s_0) {
  return s_0.m2Answers;
}
function _temp2(s) {
  return s.archetype;
}